import React, { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common/Page";
import { Badge, reviewStatusColor } from "@/pages/legal/legalConfig";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ClipboardCheck, UploadCloud, Paperclip } from "lucide-react";

const STATUSES = ["Not Sent", "Preparing Review", "Sent for Review", "Under Review",
  "Changes Requested", "Reviewed — No Changes", "Reviewed — Changes Recommended",
  "Approved", "Rejected", "Review Closed"];

export default function LegalReview() {
  const qc = useQueryClient();
  const fileRefs = useRef({});
  const [busy, setBusy] = useState(null);
  const { data: rows } = useQuery({ queryKey: ["legal-reviews"], queryFn: async () => (await api.get("/legal/reviews")).data });

  const setStatus = useMutation({
    mutationFn: async ({ id, status }) => (await api.put(`/legal/reviews/${id}`, { status })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["legal-reviews"] }); toast.success("Review updated"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const upload = async (id, file) => {
    if (!file) return;
    setBusy(id);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/legal/reviews/${id}/returned`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      qc.invalidateQueries({ queryKey: ["legal-reviews"] });
      toast.success("Returned review uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="legal-review-page">
      <PageHeader title="Legal Review" subtitle="Internal and external review records — tied to the exact document version." />
      <div className="space-y-3">
        {(rows || []).length === 0 && (
          <p className="rounded-lg border border-dashed border-[#2D3F62] px-4 py-10 text-center text-sm text-slate-500" data-testid="no-reviews">
            No review records yet. Open a template and choose "Send for Legal Review".
          </p>
        )}
        {(rows || []).map((r) => (
          <div key={r.id} className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4" data-testid={`review-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <ClipboardCheck className="h-4 w-4 text-sky-400" />
                  <span className="font-medium text-slate-100">{r.subject_name || r.subject_id}</span>
                  <span className="font-mono text-xs text-slate-400">{r.version}</span>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {r.reviewer_name} · {r.reviewer_org || r.reviewer_type} · sent {new Date(r.date_sent).toLocaleDateString()}
                  {r.due_date ? ` · due ${new Date(r.due_date).toLocaleDateString()}` : ""}
                </p>
                {r.documents?.length > 0 && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-emerald-300"><Paperclip className="h-3 w-3" /> {r.documents.length} attachment(s)</p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge value={r.status} map={reviewStatusColor} testId={`review-status-${r.id}`} />
                <Select value={r.status} onValueChange={(v) => setStatus.mutate({ id: r.id, status: v })}>
                  <SelectTrigger className="h-8 w-56 border-[#2D3F62] bg-[#0B1120] text-xs text-slate-100" data-testid={`review-status-select-${r.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <input type="file" hidden ref={(el) => (fileRefs.current[r.id] = el)} onChange={(e) => upload(r.id, e.target.files?.[0])} data-testid={`review-file-${r.id}`} />
                <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => fileRefs.current[r.id]?.click()}
                  className="border-[#2D3F62] bg-transparent text-slate-200 hover:bg-[#17233B]" data-testid={`review-upload-${r.id}`}>
                  <UploadCloud className="mr-1 h-3.5 w-3.5" /> Upload returned
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
